/* eslint-disable no-param-reassign */
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Grid } from '@mui/system';
import { isEmpty } from 'lodash';
import { CalibrationPerformanceModel } from 'api/models/CalibrationPerformanceModel';
import { GPC } from 'api/models/GPC';
import { globalAction } from 'common/actions';
import { performanceKeys, PUBLIC_COMPS_APPROACH } from 'common/constants/calibration';
import { VALUATIONS_SLUG } from 'common/constants/valuations';
import { useFormat } from 'common/hooks';
import { useStore } from 'common/store';
import { EXPORT_MODE_STYLES } from 'common/types/pdfExport';
import { UseStoreValues } from 'common/types/store';
import { type Calibration as CalibrationType, ValuationsApproach } from 'common/types/valuation';
import { CustomContainer } from 'components';
import ScalarSpreadsheet from 'components/ScalarSpreadsheet';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import CalibrationContext from 'context/CalibrationContext';
import { useLayoutContext } from 'context/LayoutContext';
import { useExportSignal } from 'features/pdfExport';
import { PdfExportTargets } from 'pages/Valuations/util/constants';
import { VALUATIONS_PUBLIC_COMPANIES_APPROACH } from 'pages/ValuationsAllocation/common/constants/valuations';
import { valuationTabId } from 'pages/ValuationsAllocation/common/utils/valuationTabId';
import { useApproachRegistry } from 'pages/ValuationsAllocation/context/ApproachRegistryContext';
import { PublicComp } from 'pages/ValuationsAllocation/types';
import { buildCalibrationPanelId } from 'pages/ValuationsAllocation/util';
import {
  CALIBRATION_MODEL_NAME,
  CUSTOM_DATE_PERFORMANCE_METRIC_MODEL_NAME,
} from 'services/hooks/useReadFieldAttributes/constants';
import { CalibrationPerformanceSheetData, CalibrationProps } from './Calibration.props';
import rowConfigCalibrationOutputs from './Outputs/rowConfigCalibrationOutputs';
import rowTransformerCalibrationOutputs from './Outputs/rowTransformerCalibrationOutputs';
import { createColumnsCalibrationPerformance, rowConfigCalibrationPerformance } from './performance_metrics/config';
import useUpdateCustomDatePerformanceMetrics from '../guidelinePublicCompanies/utils/useUpdateCustomDatePerformanceMetrics';
import { RowConfigCalibrationParams } from './performance_metrics/config/rowConfig';
import { getSelectorValues } from './utilities';
import { generateOrderMapForComps, PerformanceCalculator, readDisabledPerformances } from './utils';
import ValuationContext from '../../../ValuationsAllocation/ValuationContext';
import { useCustomClasses as usePerformanceMetricsCustomClasses } from '../guidelinePublicCompanies/PerformanceMetrics/config';
import {
  CustomCalibrationAttributes,
  GuidelinePublicCompaniesAttributesExtended,
} from '../guidelinePublicCompanies/utils/types';

const VALUATION_CALIBRATION_INPUT_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${CALIBRATION_MODEL_NAME}-input-container`;
const VALUATION_CALIBRATION_OUTPU_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${CALIBRATION_MODEL_NAME}-output-container`;
const VALUATION_CALIBRATION_PERFORMANCE_METRICS_CONTAINER_CLASS = `${VALUATIONS_SLUG}-${CUSTOM_DATE_PERFORMANCE_METRIC_MODEL_NAME}-container`;

// perf metrics info should only be re-requested to CapIQ API when
// a. calibration date changes
// b. source of comps changes
// c. rows of public comps changes (handled by another hook)

// for new calibrations, set to calibration.temp_public_comps_reference or public_comps_approach_calibration is done in parser

const Calibration = ({
  spreadsheets,
  workbook,
  onChange,
  approaches,
  financials,
  company,
  financialStatementsList,
  calibration,
}: CalibrationProps) => {
  const calibrationInputSheet = spreadsheets.calibration_inputs;
  const calibrationOutputSheet = spreadsheets.calibration_outputs;
  const calibrationPerformanceSheet = spreadsheets.calibration_performance as CalibrationPerformanceSheetData;
  const { customClasses: performanceMetricsCustomClasses } = usePerformanceMetricsCustomClasses();
  const [compsWithCustomDatePerformanceMetrics, setCompsWithCustomDatePerformanceMetrics] = useState<GPC[]>([]);
  const { resetConfiguration, gpcAttributes, calibrationAttributes, registerApproachFormat } = useContext(ValuationContext);
  const [format, formatDispatch] = useFormat();
  const [_, dispatch] = useStore() as unknown as UseStoreValues;
  const { evaluateSpreadsheets } = workbook;
  const { isExportMode } = useLayoutContext();
  const [selectedCompsSource, setSelectedCompsSource] = useState<string>('');

  const { stableIds, dependencyGraph } = useApproachRegistry();

  const { register: registerNode } = dependencyGraph;
  const { getOrCreateId } = stableIds;

  const calibrationPanelId = useMemo(
    () => calibration.panel_id ?? calibration.panelId,
    [calibration.panel_id, calibration.panelId]
  );

  useEffect(() => {
    const tabId = valuationTabId('calibration', String(calibrationPanelId ?? ''));
    registerApproachFormat?.(tabId, format, formatDispatch);
  }, [calibrationPanelId, format, formatDispatch, registerApproachFormat]);

  const availablePublicCompsApproaches = useMemo(
    () => (approaches ?? []).filter(({ approach_type }) => approach_type === VALUATIONS_PUBLIC_COMPANIES_APPROACH),
    [approaches]
  );

  // effect to set initial source of comps => gpcComparison
  useEffect(() => {
    const referenceToSourceOfComps =
      calibration.public_comps_approach_calibration ?? calibration.temp_public_comps_reference;
    if (referenceToSourceOfComps && availablePublicCompsApproaches.length > 0) {
      const selectedApproach = availablePublicCompsApproaches.find(
        approach =>
          approach.id === calibration.public_comps_approach_calibration ||
          approach.panel_id === calibration.temp_public_comps_reference
      );
      if (selectedApproach) {
        setSelectedCompsSource(selectedApproach.panel_id!.toString());
        registerNode({
          approachId: getOrCreateId(calibrationPanelId),
          approachType: 'Calibration',
          panelId: buildCalibrationPanelId(calibrationPanelId),
          sourceOfTruthType: VALUATIONS_PUBLIC_COMPANIES_APPROACH,
          sourceOfTruthStableId: getOrCreateId(selectedApproach.panelId!),
        });
      }
    }
  }, [
    calibration.public_comps_approach_calibration,
    calibration.temp_public_comps_reference,
    availablePublicCompsApproaches,
    registerNode,
    getOrCreateId,
    calibrationPanelId,
  ]);

  const sourceOfCompsApproach = useMemo(() => {
    const selectedApproach = availablePublicCompsApproaches.find(approach => approach.panelId === selectedCompsSource);
    return selectedApproach ?? ({} as ValuationsApproach);
  }, [availablePublicCompsApproaches, selectedCompsSource]);

  const gpcComparison = useMemo(
    () => sourceOfCompsApproach?.valuations_approach_gpc?.gpc_comparison ?? [],
    [sourceOfCompsApproach]
  );

  const { getCompsWithCustomDatePerformanceMetricsCalibration, validateCustomDatePerformanceMetrics } =
    useUpdateCustomDatePerformanceMetrics({
      gpcComparison: (sourceOfCompsApproach?.valuations_approach_gpc ?? []) as unknown as PublicComp[],
      format,
      gpcAttributes: gpcAttributes as unknown as GuidelinePublicCompaniesAttributesExtended,
      calibrationAttributes: calibrationAttributes as unknown as CustomCalibrationAttributes,
    });

  const updatePerformanceMetricsUI = useCallback(
    (comparisons: GPC[], calibration: CalibrationType) => {
      const performanceMetricsAsOfDateColumns = createColumnsCalibrationPerformance({
        gpcComparisonByDate: comparisons,
        financials,
      });

      const { disabledIdentifiers, disabledNames } = readDisabledPerformances({ calibration });
      const orderMap = generateOrderMapForComps(calibration.performances ?? []);

      const enabledUpdatedPerformances = comparisons.map((comp: any) => {
        const wasDisabled = disabledIdentifiers?.includes(comp.cap_iq_id) || disabledNames?.includes(comp.name);
        return { ...comp, enabled: !wasDisabled };
      });
      enabledUpdatedPerformances.sort((a: any, b: any) => {
        const orderA = orderMap.has(a.cap_iq_id) ? orderMap.get(a.cap_iq_id) : (calibration?.performances ?? []).length;
        const orderB = orderMap.has(b.cap_iq_id) ? orderMap.get(b.cap_iq_id) : (calibration?.performances ?? []).length;
        return orderA! - orderB!;
      });

      const performanceMetricsAsOfDateCommonRowConfig = {
        asOfDate: calibration.calibration_date,
        company,
        customClasses: performanceMetricsCustomClasses,
        gpcComparisonByDate: enabledUpdatedPerformances,
      };

      const rowConfig = rowConfigCalibrationPerformance({
        ...performanceMetricsAsOfDateCommonRowConfig,
      });

      const performanceMetricsKeys = performanceMetricsAsOfDateColumns.filter(item =>
        Object.keys(item).some(key => key.includes('performance'))
      );

      const numRows = performanceMetricsKeys.length;
      const numCols = Object.keys(performanceMetricsKeys[0]).length;
      calibration.performances = [];

      calibration.performances = PerformanceCalculator({
        numCols,
        numRows,
        performanceMetricsKeys,
        performanceKeys,
        idCalibration: calibration.id!,
        calibration,
      });

      performanceMetricsAsOfDateColumns.splice(0, 1);
      calibrationPerformanceSheet.reset({
        rowConfig,
        columns: performanceMetricsAsOfDateColumns,
      });

      // refreshes formulas in outputs sheets based on new comps in perf metrics
      const rowConfigOutputs = rowConfigCalibrationOutputs({
        calibration,
        columnsPerformance: performanceMetricsAsOfDateColumns,
      });

      const columnsOutputs = rowTransformerCalibrationOutputs(getSelectorValues(calibration));

      calibrationOutputSheet.reset({
        rowConfig: rowConfigOutputs,
        columns: columnsOutputs,
      });

      resetConfiguration?.();
    },
    [
      calibrationPerformanceSheet,
      calibrationOutputSheet,
      company,
      financials,
      performanceMetricsCustomClasses,
      resetConfiguration,
    ]
  );

  // retrieves new data and updates compsWithCustomDatePerformanceMetrics state to trigger UI update via updatePerformanceMetricsUI
  const handleAsOfDateAlias = useCallback(
    async (newDate: string) => {
      if (Array.isArray(gpcComparison) && !isEmpty(gpcComparison)) {
        dispatch(globalAction.showLoadingProgress(true));
        const tmpCompsWithCustomDatePerformanceMetrics = (await getCompsWithCustomDatePerformanceMetricsCalibration(
          newDate,
          gpcComparison
        )) as CalibrationPerformanceModel[];

        const compsWithValidatedCustomDatePerformanceMetrics = validateCustomDatePerformanceMetrics(
          tmpCompsWithCustomDatePerformanceMetrics
        ) as CalibrationPerformanceModel[];

        const reEnabledPerformanceMetrics = compsWithValidatedCustomDatePerformanceMetrics.map(comp => ({
          ...comp,
          enabled: true,
        })) as GPC[];

        setCompsWithCustomDatePerformanceMetrics(reEnabledPerformanceMetrics);
      }
    },
    [gpcComparison, dispatch, getCompsWithCustomDatePerformanceMetricsCalibration, validateCustomDatePerformanceMetrics]
  );

  const handleSourceOfCompsChange = useCallback(
    async (newSource: string) => {
      setSelectedCompsSource(newSource);
      const newSourceOfCompsApproach = availablePublicCompsApproaches.find(approach => approach.panelId === newSource);
      const newComparisons = newSourceOfCompsApproach?.valuations_approach_gpc?.gpc_comparison ?? [];
      if (newComparisons.length && calibration.calibration_date) {
        dispatch(globalAction.showLoadingProgress(true));
        try {
          const tmpCompsWithCustomDatePerformanceMetrics = (await getCompsWithCustomDatePerformanceMetricsCalibration(
            calibration.calibration_date,
            newComparisons
          )) as CalibrationPerformanceModel[];

          const compsWithValidatedCustomDatePerformanceMetrics = validateCustomDatePerformanceMetrics(
            tmpCompsWithCustomDatePerformanceMetrics
          ) as CalibrationPerformanceModel[];

          const reEnabledPerformanceMetrics = compsWithValidatedCustomDatePerformanceMetrics.map(comp => ({
            ...comp,
            enabled: true,
          })) as GPC[];

          setCompsWithCustomDatePerformanceMetrics(reEnabledPerformanceMetrics);
        } finally {
          dispatch(globalAction.showLoadingProgress(false));
        }
      }
    },
    [
      availablePublicCompsApproaches,
      calibration.calibration_date,
      dispatch,
      getCompsWithCustomDatePerformanceMetricsCalibration,
      validateCustomDatePerformanceMetrics,
    ]
  );

  const handlePerformanceRowEnabledChange = useCallback(
    async cell => {
      const performanceToUpdate = calibrationPerformanceSheet.tableData.gpcComparisonByDate.find(
        performance =>
          performance.cap_iq_id?.toLowerCase() === cell.rowHeaderId.toLowerCase() || cell.value === performance.name
      );
      if (!performanceToUpdate) {
        return;
      }
      performanceToUpdate.enabled = cell.enabled;
      const columns = {
        valuationsApproachGpc: {} as RowConfigCalibrationParams['valuationsApproachGpc'],
        financials,
        gpcComparisonByDate: calibrationPerformanceSheet.tableData.gpcComparisonByDate,
      };

      const columnsPerformance = createColumnsCalibrationPerformance(columns);
      // to update expressions based on new enabled/disabled rows
      await calibrationOutputSheet.reset({
        rowConfig: rowConfigCalibrationOutputs({
          calibration,
          columnsPerformance,
          isDisabled: false,
        }),
      });
      await calibrationPerformanceSheet.reset({
        rowConfig: rowConfigCalibrationPerformance({
          company,
          gpcComparisonByDate: calibrationPerformanceSheet.tableData.gpcComparisonByDate,
        }),
      });
      await evaluateSpreadsheets([calibrationInputSheet, calibrationPerformanceSheet, calibrationOutputSheet]);
    },
    [
      calibration,
      financials,
      evaluateSpreadsheets,
      company,
      calibrationPerformanceSheet,
      calibrationInputSheet,
      calibrationOutputSheet,
    ]
  );

  const handleOnChange = useCallback(
    async (cell, value) => {
      onChange(cell, value);
      if (cell.alias === 'calibration_date') {
        const newDate = value;
        await handleAsOfDateAlias(newDate);
      } else if (cell.isEditableTitleCell && cell.isComparisonRow) {
        await handlePerformanceRowEnabledChange(cell);
      } else if (cell.alias === PUBLIC_COMPS_APPROACH) {
        handleSourceOfCompsChange(value);
      }
    },
    [handleAsOfDateAlias, onChange, handlePerformanceRowEnabledChange, handleSourceOfCompsChange]
  );

  const updateCustomDatePerformanceMetrics = useCallback(async () => {
    dispatch(globalAction.showLoadingProgress(false));
    updatePerformanceMetricsUI(compsWithCustomDatePerformanceMetrics, calibration);
  }, [compsWithCustomDatePerformanceMetrics, calibration, dispatch, updatePerformanceMetricsUI]);

  // compsWithCustomDatePerformanceMetrics state is only populated once the date or source of comps changes,
  // so this effect is in charge of triggering the UI re-render
  useEffect(() => {
    if (!isEmpty(compsWithCustomDatePerformanceMetrics)) {
      updateCustomDatePerformanceMetrics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compsWithCustomDatePerformanceMetrics]);

  // effect to populate perf metrics table when the calibration is new
  useEffect(() => {
    const doFirstPerformanceGeneration = async () => {
      await handleSourceOfCompsChange(selectedCompsSource);
    };
    // there's a source of comps but the performance have not been generated because this is new
    if (calibration.performances!.length === 0 && gpcComparison.length > 0) {
      doFirstPerformanceGeneration();
    }
  }, [calibration.performances, gpcComparison, handleSourceOfCompsChange, selectedCompsSource]);

  const GroupContextValue = useMemo(
    () => ({
      approaches,
      calibrationPerformanceSheet,
      calibrationOutputSheet,
      calibrationInputSheet,
      financials,
      company,
      performanceMetricsCustomClasses,
      financialStatementsList,
      calibration,
    }),
    [
      approaches,
      calibrationPerformanceSheet,
      calibrationOutputSheet,
      calibrationInputSheet,
      financials,
      company,
      performanceMetricsCustomClasses,
      financialStatementsList,
      calibration,
    ]
  );
  useExportSignal({
    target: PdfExportTargets.CALIBRATION.id,
    status:
      !calibrationInputSheet?.data || !calibrationOutputSheet?.data || !calibrationPerformanceSheet?.data
        ? 'loading'
        : 'ready',
    enabled: isExportMode,
  });

  if (isExportMode) {
    return (
      <div data-ref={PdfExportTargets.CALIBRATION.id} style={EXPORT_MODE_STYLES}>
        <CalibrationContext.Provider value={GroupContextValue}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '24px' }}>
            <ScalarSpreadsheet
              {...(calibrationInputSheet as unknown as SpreadsheetConfig)}
              sheet={calibrationInputSheet}
              workbook={workbook}
              onChange={handleOnChange}
              format={format}
              formatDispatch={formatDispatch}
            />
            <ScalarSpreadsheet
              {...(calibrationOutputSheet as unknown as SpreadsheetConfig)}
              sheet={calibrationOutputSheet}
              workbook={workbook}
              onChange={handleOnChange}
              format={format}
              formatDispatch={formatDispatch}
            />
          </div>
        </CalibrationContext.Provider>
        <ScalarSpreadsheet
          {...(calibrationPerformanceSheet as unknown as SpreadsheetConfig)}
          sheet={calibrationPerformanceSheet}
          workbook={workbook}
          onChange={handleOnChange}
          format={format}
          formatDispatch={formatDispatch}
        />
      </div>
    );
  }

  return (
    <CustomContainer>
      <CalibrationContext.Provider value={GroupContextValue}>
        <Grid className={VALUATION_CALIBRATION_INPUT_CONTAINER_CLASS}>
          <ScalarSpreadsheet
            {...(calibrationInputSheet as unknown as SpreadsheetConfig)}
            sheet={calibrationInputSheet}
            workbook={workbook}
            onChange={handleOnChange}
            format={format}
            formatDispatch={formatDispatch}
          />
        </Grid>

        <Grid className={VALUATION_CALIBRATION_OUTPU_CONTAINER_CLASS}>
          <ScalarSpreadsheet
            {...(calibrationOutputSheet as unknown as SpreadsheetConfig)}
            sheet={calibrationOutputSheet}
            workbook={workbook}
            onChange={handleOnChange}
            format={format}
            formatDispatch={formatDispatch}
          />
        </Grid>
      </CalibrationContext.Provider>

      <Grid className={VALUATION_CALIBRATION_PERFORMANCE_METRICS_CONTAINER_CLASS} size={{ xs: 12 }}>
        <ScalarSpreadsheet
          {...(calibrationPerformanceSheet as unknown as SpreadsheetConfig)}
          sheet={calibrationPerformanceSheet}
          workbook={workbook}
          onChange={handleOnChange}
          format={format}
          formatDispatch={formatDispatch}
        />
      </Grid>
    </CustomContainer>
  );
};

export default Calibration;
